import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { adminApi } from "@/lib/admin";
import { API } from "@/lib/api";
import { ArrowLeft, Loader2, RefreshCw, AlertTriangle } from "lucide-react";
import LiveSessionsMap from "@/components/LiveSessionsMap";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

const TOKEN_LIMIT_PRESETS = [
  { id: "2m", label: "2M", monthly_token_cap: 2_000_000, daily_message_cap: 100, monthly_message_cap: 1_000 },
  { id: "5m", label: "5M", monthly_token_cap: 5_000_000, daily_message_cap: 250, monthly_message_cap: 2_500 },
  { id: "10m", label: "10M", monthly_token_cap: 10_000_000, daily_message_cap: 500, monthly_message_cap: 5_000 },
];

// ─── Layout primitives ────────────────────────────────────────────────

function Card({ title, description, children, testid, action }) {
  return (
    <section
      data-testid={testid}
      className="border border-[var(--border)] p-6 md:p-7 mb-5"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div className="min-w-0">
          <h3 className="font-mono-ui text-[11px] tracking-[0.34em] uppercase text-[var(--text)]">
            {title}
          </h3>
          {description && (
            <p className="font-sans text-sm text-[var(--text-muted)] leading-relaxed mt-2 max-w-3xl">
              {description}
            </p>
          )}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function KpiCell({ label, value, sub, testid }) {
  return (
    <div
      data-testid={testid}
      className="px-4 py-3 border-r border-[var(--border)] last:border-r-0 min-w-0"
    >
      <div className="font-mono-ui text-[9px] tracking-[0.26em] uppercase text-[var(--text-dim)] truncate">
        {label}
      </div>
      <div className="font-serif text-2xl md:text-3xl text-[var(--text)] mt-1 truncate">
        {value ?? "—"}
      </div>
      {sub && (
        <div className="font-mono-ui text-[9px] tracking-[0.2em] uppercase text-[var(--text-dim)] mt-1 truncate">
          {sub}
        </div>
      )}
    </div>
  );
}

function RangeToggle({ value, onChange, testid }) {
  const ranges = [
    { v: 7, l: "7 days" },
    { v: 30, l: "30 days" },
    { v: 90, l: "90 days" },
  ];
  return (
    <div className="inline-flex border border-[var(--border)]" data-testid={testid}>
      {ranges.map((r) => (
        <button
          key={r.v}
          onClick={() => onChange(r.v)}
          data-testid={`range-${r.v}`}
          className={`px-3 py-1.5 font-mono-ui text-[10px] tracking-[0.24em] uppercase transition-colors ${
            value === r.v
              ? "bg-[var(--accent)] text-[var(--bg)]"
              : "text-[var(--text-muted)] hover:text-[var(--text)]"
          }`}
        >
          {r.l}
        </button>
      ))}
    </div>
  );
}

// ─── Chart primitives (brutalist, one-color per metric) ───────────────

const CHART_COLORS = {
  pageviews: "#1e40af",
  unique_visitors: "#c2410c",
  chats: "#059669",
  signups: "#7c3aed",
  voice_minutes: "#db2777",
  failover_hits: "#dc2626",
};

function TimeSeriesChart({ buckets, keys, testid }) {
  if (!buckets || buckets.length === 0)
    return (
      <p className="font-sans text-sm text-[var(--text-dim)]">No data in range.</p>
    );
  return (
    <div className="w-full h-72 min-h-72 min-w-0" data-testid={testid}>
      <ResponsiveContainer width="99%" height={260}>
        <LineChart data={buckets} margin={{ top: 8, right: 20, bottom: 0, left: 0 }}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="2 4" vertical={false} />
          <XAxis
            dataKey="date"
            tickFormatter={(d) => d.slice(5)}
            tick={{ fill: "var(--text-dim)", fontSize: 10 }}
            stroke="var(--border)"
          />
          <YAxis
            tick={{ fill: "var(--text-dim)", fontSize: 10 }}
            stroke="var(--border)"
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={{
              background: "var(--bg)",
              border: "1px solid var(--border)",
              fontFamily: "monospace",
              fontSize: 12,
            }}
            labelStyle={{ color: "var(--text)" }}
          />
          <Legend wrapperStyle={{ fontFamily: "monospace", fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase" }} />
          {keys.map((k) => (
            <Line
              key={k}
              type="monotone"
              dataKey={k}
              stroke={CHART_COLORS[k] || "var(--accent)"}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function BarBreakdown({ rows, labelKey, valueKey, testid }) {
  if (!rows || rows.length === 0)
    return <p className="font-sans text-sm text-[var(--text-dim)]">No data.</p>;
  return (
    <div className="w-full h-64 min-h-64 min-w-0" data-testid={testid}>
      <ResponsiveContainer width="99%" height={240}>
        <BarChart data={rows.slice(0, 12)} layout="vertical" margin={{ left: 20, right: 20 }}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="2 4" horizontal={false} />
          <XAxis type="number" tick={{ fill: "var(--text-dim)", fontSize: 10 }} stroke="var(--border)" allowDecimals={false} />
          <YAxis
            dataKey={labelKey}
            type="category"
            width={140}
            tick={{ fill: "var(--text-dim)", fontSize: 10 }}
            stroke="var(--border)"
          />
          <Tooltip
            contentStyle={{
              background: "var(--bg)",
              border: "1px solid var(--border)",
              fontFamily: "monospace",
              fontSize: 12,
            }}
          />
          <Bar dataKey={valueKey} fill="var(--accent)" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────

export default function AdminAnalytics() {
  const { user } = useAuth();

  const [range, setRange] = useState(30);
  const [overview, setOverview] = useState(null);
  const [timeseries, setTimeseries] = useState(null);
  const [geo, setGeo] = useState(null);
  const [devices, setDevices] = useState(null);
  const [traffic, setTraffic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [profileEmail, setProfileEmail] = useState("");
  const [profileUserId, setProfileUserId] = useState("");
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState("");

  // Per-user limits editor
  const [limitTokens, setLimitTokens] = useState("");
  const [limitQueries, setLimitQueries] = useState("");
  const [limitMonthlyMessages, setLimitMonthlyMessages] = useState("");
  const [limitBusy, setLimitBusy] = useState(false);
  const [limitMsg, setLimitMsg] = useState("");

  // Retention email
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailMsg, setEmailMsg] = useState("");

  // Live sessions + anomalies + session replay
  const [liveSessions, setLiveSessions] = useState(null);
  const [anomalies, setAnomalies] = useState(null);
  const [sessionsList, setSessionsList] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionEvents, setSessionEvents] = useState(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const [ov, ts, g, dv, tf, ls, an, ss] = await Promise.all([
        adminApi.analyticsOverview(),
        adminApi.analyticsTimeseries(range),
        adminApi.analyticsGeo(range),
        adminApi.analyticsDevices(range),
        adminApi.analyticsTraffic(range),
        adminApi.analyticsLiveSessions(15),
        adminApi.analyticsAnomalies(),
        adminApi.analyticsSessions(7, 100),
      ]);
      setOverview(ov);
      setTimeseries(ts);
      setGeo(g);
      setDevices(dv);
      setTraffic(tf);
      setLiveSessions(ls);
      setAnomalies(an);
      setSessionsList(ss);
    } catch (e) {
      // no-op; keep last data
      console.error(e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Auto-refresh live sessions every 30s (independent from range change)
  useEffect(() => {
    if (!(user && user.role === "admin")) return;
    const t = setInterval(async () => {
      try {
        const ls = await adminApi.analyticsLiveSessions(15);
        setLiveSessions(ls);
      } catch { /* ignore */ }
    }, 30000);
    return () => clearInterval(t);
  }, [user]);

  const openSession = async (sid) => {
    setSelectedSession(sid);
    setSessionEvents(null);
    try {
      const res = await adminApi.analyticsSession(sid);
      setSessionEvents(res);
    } catch (err) {
      setSessionEvents({ error: err.message || "Failed to load session." });
    }
  };

  useEffect(() => {
    if (user && user.role === "admin") load();
  }, [user, range]);

  const buckets = timeseries?.buckets || [];

  const lookupUser = async (e) => {
    e.preventDefault();
    const id = profileUserId.trim();
    if (!id) return;
    setProfileLoading(true);
    setProfileError("");
    setUserProfile(null);
    setLimitMsg("");
    setEmailMsg("");
    try {
      const res = await adminApi.analyticsUser(id);
      setUserProfile(res);
      const lim = res?.user?.limits || {};
      setLimitTokens(
        typeof lim.monthly_token_cap === "number" ? String(lim.monthly_token_cap) : ""
      );
      setLimitQueries(
        typeof lim.daily_message_cap === "number" ? String(lim.daily_message_cap) : ""
      );
      setLimitMonthlyMessages(
        typeof lim.monthly_message_cap === "number" ? String(lim.monthly_message_cap) : ""
      );
    } catch (err) {
      setProfileError(err.message || "Lookup failed.");
    } finally {
      setProfileLoading(false);
    }
  };

  const saveLimits = async () => {
    if (!userProfile || limitBusy) return;
    setLimitBusy(true);
    setLimitMsg("");
    try {
      const body = {};
      body.monthly_token_cap = limitTokens.trim() === "" ? null : parseInt(limitTokens, 10);
      body.daily_message_cap = limitQueries.trim() === "" ? null : parseInt(limitQueries, 10);
      body.monthly_message_cap = limitMonthlyMessages.trim() === "" ? null : parseInt(limitMonthlyMessages, 10);
      await adminApi.setUserLimits(userProfile.user.id, body);
      setLimitMsg("Saved.");
      // reload profile to reflect saved limits
      const res = await adminApi.analyticsUser(userProfile.user.id);
      setUserProfile(res);
    } catch (err) {
      setLimitMsg(err.message || "Save failed.");
    } finally {
      setLimitBusy(false);
    }
  };

  const clearLimits = async () => {
    if (!userProfile || limitBusy) return;
    setLimitBusy(true);
    setLimitMsg("");
    try {
      await adminApi.setUserLimits(userProfile.user.id, {
        monthly_token_cap: null,
        daily_message_cap: null,
        monthly_message_cap: null,
        tier: null,
      });
      setLimitTokens("");
      setLimitQueries("");
      setLimitMonthlyMessages("");
      setLimitMsg("Cleared — user reverts to defaults.");
      const res = await adminApi.analyticsUser(userProfile.user.id);
      setUserProfile(res);
    } catch (err) {
      setLimitMsg(err.message || "Clear failed.");
    } finally {
      setLimitBusy(false);
    }
  };

  const applyLimitPreset = async (preset) => {
    if (!userProfile || limitBusy) return;
    setLimitBusy(true);
    setLimitMsg("");
    try {
      await adminApi.setUserLimits(userProfile.user.id, {
        tier: preset.id,
        monthly_token_cap: preset.monthly_token_cap,
        daily_message_cap: preset.daily_message_cap,
        monthly_message_cap: preset.monthly_message_cap,
      });
      setLimitTokens(String(preset.monthly_token_cap));
      setLimitQueries(String(preset.daily_message_cap));
      setLimitMonthlyMessages(String(preset.monthly_message_cap));
      setLimitMsg(`${preset.label} tier applied.`);
      const res = await adminApi.analyticsUser(userProfile.user.id);
      setUserProfile(res);
    } catch (err) {
      setLimitMsg(err.message || "Preset failed.");
    } finally {
      setLimitBusy(false);
    }
  };

  const sendRetentionEmail = async (kind) => {
    if (!userProfile || emailBusy) return;
    setEmailBusy(true);
    setEmailMsg("");
    try {
      const res = await adminApi.sendRetentionEmail(userProfile.user.id, kind);
      setEmailMsg(res?.sent ? `Sent ${kind} email → ${res.to}` : "Queued.");
    } catch (err) {
      setEmailMsg(err.message || "Send failed.");
    } finally {
      setEmailBusy(false);
    }
  };

  const kpi = useMemo(() => overview || {}, [overview]);

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg)] text-[var(--text-muted)] font-mono-ui text-[11px] tracking-[0.3em] uppercase">
        Loading…
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" state={{ from: "/admin/analytics" }} replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <header className="border-b border-[var(--border)] px-6 md:px-10 py-5 flex items-center justify-between">
        <Link
          to="/admin?tab=usage"
          data-testid="analytics-back"
          className="font-mono-ui text-[11px] tracking-[0.28em] uppercase text-[var(--text-muted)] hover:text-[var(--text)] inline-flex items-center gap-2 transition-colors"
        >
          <ArrowLeft size={13} /> Back to admin
        </Link>
        <h1 className="font-mono-ui text-[11px] tracking-[0.4em] uppercase">Analytics</h1>
        <div className="flex items-center gap-4 flex-wrap">
          <RangeToggle value={range} onChange={setRange} testid="range-toggle" />
          <div className="inline-flex border border-[var(--border)]" data-testid="export-toggle">
            {["csv", "pdf", "docx"].map((f) => (
              <a
                key={f}
                href={`${API}/admin/analytics/export?format=${f}&days=${range}`}
                data-testid={`export-${f}`}
                className="px-3 py-1.5 font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-muted)] hover:text-[var(--accent)] transition-colors border-r border-[var(--border)] last:border-r-0"
              >
                {f}
              </a>
            ))}
          </div>
          <button
            onClick={() => load(true)}
            disabled={refreshing}
            data-testid="analytics-refresh"
            className="font-mono-ui text-[10px] tracking-[0.24em] uppercase inline-flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--text)] disabled:opacity-40"
          >
            <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </header>

      {/* KPI Strip */}
      <div className="border-b border-[var(--border)] bg-[var(--bg)]">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div
            className="grid grid-cols-2 md:grid-cols-6 border-l border-r border-[var(--border)]"
            data-testid="analytics-kpi-strip"
          >
            <KpiCell
              testid="kpi-pageviews-24h"
              label="Pageviews 24h"
              value={kpi.pageviews?.h24?.toLocaleString?.() ?? "—"}
              sub={`7d ${kpi.pageviews?.d7?.toLocaleString?.() ?? "—"}`}
            />
            <KpiCell
              testid="kpi-visitors-24h"
              label="Visitors 24h"
              value={kpi.unique_visitors?.h24?.toLocaleString?.() ?? "—"}
              sub={`7d ${kpi.unique_visitors?.d7?.toLocaleString?.() ?? "—"}`}
            />
            <KpiCell
              testid="kpi-dau"
              label="DAU / WAU / MAU"
              value={kpi.active_users?.dau ?? "—"}
              sub={`${kpi.active_users?.wau ?? "—"} · ${kpi.active_users?.mau ?? "—"}`}
            />
            <KpiCell
              testid="kpi-signups-7d"
              label="Signups 7d"
              value={kpi.signups?.d7 ?? "—"}
              sub={`30d ${kpi.signups?.d30 ?? "—"}`}
            />
            <KpiCell
              testid="kpi-chats-7d"
              label="Chats 7d"
              value={kpi.chats?.d7?.toLocaleString?.() ?? "—"}
              sub={`24h ${kpi.chats?.h24?.toLocaleString?.() ?? "—"}`}
            />
            <KpiCell
              testid="kpi-countries"
              label="Countries"
              value={(geo?.countries || []).length}
              sub={`in ${range}d`}
            />
          </div>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-8 pb-20">
        {loading && (
          <div className="flex items-center gap-2 mb-6 text-[var(--text-muted)] font-mono-ui text-[10px] tracking-[0.24em] uppercase">
            <Loader2 size={12} className="animate-spin" /> Loading analytics…
          </div>
        )}

        {/* Anomaly banner — only shown when severity != ok */}
        {anomalies && anomalies.worst && anomalies.worst !== "ok" && (
          <div
            data-testid="anomaly-banner"
            className={`border-2 mb-5 p-4 ${
              anomalies.worst === "critical"
                ? "border-red-500 bg-red-500/5"
                : "border-amber-500 bg-amber-500/5"
            }`}
          >
            <div className="flex items-start gap-3">
              <AlertTriangle
                size={18}
                className={anomalies.worst === "critical" ? "text-red-500" : "text-amber-500"}
              />
              <div className="flex-1">
                <div className="font-mono-ui text-[11px] tracking-[0.28em] uppercase mb-2">
                  {anomalies.worst === "critical" ? "Critical drop detected" : "Anomaly warning"}
                </div>
                <ul className="space-y-1 font-sans text-sm">
                  {(anomalies.findings || [])
                    .filter((f) => f.severity !== "ok")
                    .map((f) => (
                      <li key={f.metric} data-testid={`anomaly-${f.metric}`}>
                        <span className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--text-muted)]">
                          {f.metric}
                        </span>
                        <span className="ml-2">
                          today <strong>{f.today}</strong> vs 7-day avg{" "}
                          <strong>{f.baseline_7d_avg}</strong> — down{" "}
                          <strong className="text-red-500">{f.drop_pct}%</strong>
                        </span>
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        <Card
          title="Live sessions"
          testid="section-live-sessions"
          description={`Visitors active in the last 15 minutes. Auto-refreshes every 30s. Dots pulse at each visitor's approximate city.`}
          action={
            <span
              className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-muted)]"
              data-testid="live-count"
            >
              {liveSessions?.count ?? 0} active
            </span>
          }
        >
          <LiveSessionsMap dots={liveSessions?.dots || []} testid="chart-live-map" />
          {(liveSessions?.sessions || []).length > 0 && (
            <div className="mt-4 max-h-56 overflow-y-auto border-t border-[var(--border)] pt-3">
              <table className="w-full font-mono-ui text-[11px]">
                <thead>
                  <tr className="text-[var(--text-dim)] tracking-[0.2em] uppercase text-[9px]">
                    <th className="text-left py-1.5">Visitor</th>
                    <th className="text-left py-1.5">City</th>
                    <th className="text-left py-1.5">Path</th>
                    <th className="text-left py-1.5">Device</th>
                    <th className="text-right py-1.5">Pages</th>
                    <th className="text-right py-1.5">Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {(liveSessions?.sessions || []).slice(0, 25).map((s) => (
                    <tr key={s.visitor} className="border-t border-[var(--border)]/60">
                      <td className="py-1.5 text-[var(--text)] truncate max-w-[160px]">
                        {s.email || s.visitor}
                      </td>
                      <td className="py-1.5 text-[var(--text-muted)]">
                        {s.city ? `${s.city}, ${s.country}` : s.country}
                      </td>
                      <td className="py-1.5 text-[var(--text-muted)] truncate max-w-[200px]">
                        {s.last_path}
                      </td>
                      <td className="py-1.5 text-[var(--text-muted)]">
                        {s.device}/{s.browser}
                      </td>
                      <td className="py-1.5 text-right">{s.page_count}</td>
                      <td className="py-1.5 text-right text-[var(--text-dim)]">
                        {s.last_ts?.slice?.(11, 19)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card
          title="Traffic over time"
          testid="section-timeseries-traffic"
          description={`Daily pageviews and unique visitors over the last ${range} days. Pageviews count every route change (React SPA). Unique visitors are distinct signed-in users + browser IDs.`}
        >
          <TimeSeriesChart
            buckets={buckets}
            keys={["pageviews", "unique_visitors"]}
            testid="chart-traffic"
          />
        </Card>

        <Card
          title="User actions over time"
          testid="section-timeseries-actions"
          description="Chats sent, new signups, and voice minutes consumed per day."
        >
          <TimeSeriesChart
            buckets={buckets}
            keys={["chats", "signups", "voice_minutes"]}
            testid="chart-actions"
          />
        </Card>

        <Card
          title="Reliability — engine failover hits"
          testid="section-timeseries-reliability"
          description="Every time the Nscale → Avian → Gemini → OpenAI → Claude ladder walked because of an engine failure. Zero is healthy."
        >
          <TimeSeriesChart
            buckets={buckets}
            keys={["failover_hits"]}
            testid="chart-failover"
          />
        </Card>

        <div className="grid md:grid-cols-2 gap-5">
          <Card
            title="Countries"
            testid="section-geo-countries"
            description={`Top countries by pageview in the last ${range} days.`}
          >
            <BarBreakdown
              rows={geo?.countries || []}
              labelKey="country"
              valueKey="pageviews"
              testid="chart-countries"
            />
            <div className="mt-4 max-h-56 overflow-y-auto border-t border-[var(--border)] pt-3">
              <table className="w-full font-mono-ui text-[11px]">
                <thead>
                  <tr className="text-[var(--text-dim)] tracking-[0.2em] uppercase text-[9px]">
                    <th className="text-left py-1.5">Country</th>
                    <th className="text-right py-1.5">Views</th>
                    <th className="text-right py-1.5">Visitors</th>
                  </tr>
                </thead>
                <tbody>
                  {(geo?.countries || []).map((c) => (
                    <tr
                      key={`${c.country_code}-${c.country}`}
                      data-testid={`geo-row-${c.country_code}`}
                      className="border-t border-[var(--border)]/60"
                    >
                      <td className="py-1.5 text-[var(--text)]">{c.country}</td>
                      <td className="py-1.5 text-right">{c.pageviews}</td>
                      <td className="py-1.5 text-right">{c.visitors}</td>
                    </tr>
                  ))}
                  {(geo?.countries || []).length === 0 && (
                    <tr>
                      <td colSpan={3} className="py-3 text-[var(--text-dim)]">
                        No geo data yet — will populate as visitors come in.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          <Card
            title="Cities"
            testid="section-geo-cities"
            description="Top 20 cities. Populated from anonymized IP geo-lookup."
          >
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full font-mono-ui text-[11px]">
                <thead>
                  <tr className="text-[var(--text-dim)] tracking-[0.2em] uppercase text-[9px]">
                    <th className="text-left py-1.5">City</th>
                    <th className="text-left py-1.5">Country</th>
                    <th className="text-right py-1.5">Views</th>
                  </tr>
                </thead>
                <tbody>
                  {(geo?.cities || []).map((c, i) => (
                    <tr key={i} className="border-t border-[var(--border)]/60">
                      <td className="py-1.5 text-[var(--text)]">{c.city}</td>
                      <td className="py-1.5 text-[var(--text-muted)]">{c.country}</td>
                      <td className="py-1.5 text-right">{c.pageviews}</td>
                    </tr>
                  ))}
                  {(geo?.cities || []).length === 0 && (
                    <tr>
                      <td colSpan={3} className="py-3 text-[var(--text-dim)]">
                        No city data yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          <Card title="Device" testid="section-device" description="Split by device class.">
            <BarBreakdown
              rows={devices?.device || []}
              labelKey="label"
              valueKey="count"
              testid="chart-device"
            />
          </Card>
          <Card title="Browser" testid="section-browser" description="Top browsers.">
            <BarBreakdown
              rows={devices?.browser || []}
              labelKey="label"
              valueKey="count"
              testid="chart-browser"
            />
          </Card>
          <Card title="OS" testid="section-os" description="Top operating systems.">
            <BarBreakdown
              rows={devices?.os || []}
              labelKey="label"
              valueKey="count"
              testid="chart-os"
            />
          </Card>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <Card title="Top pages" testid="section-top-pages" description="Most-visited routes.">
            <div className="max-h-72 overflow-y-auto">
              <table className="w-full font-mono-ui text-[11px]">
                <thead>
                  <tr className="text-[var(--text-dim)] tracking-[0.2em] uppercase text-[9px]">
                    <th className="text-left py-1.5">Path</th>
                    <th className="text-right py-1.5">Views</th>
                  </tr>
                </thead>
                <tbody>
                  {(traffic?.top_paths || []).map((p, i) => (
                    <tr key={i} className="border-t border-[var(--border)]/60">
                      <td className="py-1.5 text-[var(--text)] truncate max-w-[280px]">{p.path}</td>
                      <td className="py-1.5 text-right">{p.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
          <Card title="Referrers" testid="section-referrers" description="Where traffic comes from.">
            <div className="max-h-72 overflow-y-auto">
              <table className="w-full font-mono-ui text-[11px]">
                <thead>
                  <tr className="text-[var(--text-dim)] tracking-[0.2em] uppercase text-[9px]">
                    <th className="text-left py-1.5">Source</th>
                    <th className="text-right py-1.5">Views</th>
                  </tr>
                </thead>
                <tbody>
                  {(traffic?.referrers || []).map((r, i) => (
                    <tr key={i} className="border-t border-[var(--border)]/60">
                      <td className="py-1.5 text-[var(--text)] truncate max-w-[280px]">{r.referrer}</td>
                      <td className="py-1.5 text-right">{r.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        <Card
          title="User profile lookup"
          testid="section-user-profile"
          description="Enter a user's ID to see their full activity — chats, tokens, memories, voice minutes, IPs, countries, and their recent chat previews."
        >
          <form onSubmit={lookupUser} className="flex flex-wrap items-end gap-3 mb-5">
            <div className="flex flex-col flex-1 min-w-[240px]">
              <label className="font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)] mb-1">
                Email or user ID
              </label>
              <input
                type="text"
                value={profileUserId}
                onChange={(e) => setProfileUserId(e.target.value)}
                placeholder="friend@example.com  ·  or  ·  paste user id"
                data-testid="profile-user-id"
                className="bg-transparent border-b border-[var(--border)] focus:border-[var(--accent)] outline-none py-2 text-sm transition-colors"
              />
            </div>
            <button
              type="submit"
              disabled={profileLoading || !profileUserId.trim()}
              data-testid="profile-lookup-submit"
              className="px-5 py-2 font-mono-ui text-[10px] tracking-[0.28em] uppercase border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-[var(--bg)] disabled:opacity-40 transition-colors inline-flex items-center gap-2"
            >
              {profileLoading && <Loader2 size={12} className="animate-spin" />}
              {profileLoading ? "Loading…" : "Look up"}
            </button>
          </form>
          {profileError && (
            <p className="font-sans text-sm text-red-400 border-l-2 border-red-400 pl-3 mb-4" data-testid="profile-error">
              {profileError}
            </p>
          )}
          {userProfile && (
            <div data-testid="profile-result" className="space-y-5">
              <div className="border border-[var(--border)] p-4">
                <div className="font-serif text-xl">{userProfile.user.email}</div>
                <div className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--text-dim)] mt-1">
                  {userProfile.user.role} · {userProfile.user.tier} · joined{" "}
                  {userProfile.user.created_at?.slice?.(0, 10)}
                  {userProfile.user.deleted_at && " · DELETED"}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 border border-[var(--border)]">
                <KpiCell label="Chats 30d" value={userProfile.activity.chats_30d} />
                <KpiCell label="Chats total" value={userProfile.activity.chats_total} />
                <KpiCell label="Tokens 30d" value={userProfile.activity.tokens_30d?.toLocaleString?.() ?? "—"} />
                <KpiCell label="Voice min" value={userProfile.activity.voice_minutes_total} />
                <KpiCell label="Memories" value={userProfile.activity.memories} />
              </div>

              {/* Per-user limit override */}
              <div className="border border-[var(--border)] p-4" data-testid="limits-editor">
                <div className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-muted)] mb-1">
                  Per-user limits
                </div>
                <p className="font-sans text-xs text-[var(--text-dim)] mb-4 max-w-2xl">
                  Defaults stay unchanged. Apply 2M / 5M / 10M only when a user requests more monthly tokens. Message caps are chat-only; voice remains separate.
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {TOKEN_LIMIT_PRESETS.map((preset) => (
                    <button
                      key={preset.id}
                      onClick={() => applyLimitPreset(preset)}
                      disabled={limitBusy}
                      data-testid={`analytics-limit-preset-${preset.id}`}
                      className="px-3 py-2 font-mono-ui text-[10px] tracking-[0.24em] uppercase border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] disabled:opacity-40 transition-colors"
                      title={`${preset.monthly_token_cap.toLocaleString()} tokens/month · ${preset.daily_message_cap.toLocaleString()} messages/day · ${preset.monthly_message_cap.toLocaleString()} messages/month`}
                    >
                      Apply {preset.label}
                    </button>
                  ))}
                </div>
                <div className="grid md:grid-cols-3 gap-3 items-end max-w-3xl">
                  <div>
                    <label className="font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)] block mb-1">
                      Monthly tokens
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="10000"
                      value={limitTokens}
                      onChange={(e) => setLimitTokens(e.target.value)}
                      placeholder="e.g. 5000000"
                      data-testid="limit-tokens-input"
                      className="w-full bg-transparent border-b border-[var(--border)] focus:border-[var(--accent)] outline-none py-2 font-mono-ui text-sm transition-colors"
                    />
                  </div>
                  <div>
                    <label className="font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)] block mb-1">
                      Daily queries
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={limitQueries}
                      onChange={(e) => setLimitQueries(e.target.value)}
                      placeholder="e.g. 200"
                      data-testid="limit-queries-input"
                      className="w-full bg-transparent border-b border-[var(--border)] focus:border-[var(--accent)] outline-none py-2 font-mono-ui text-sm transition-colors"
                    />
                  </div>
                  <div>
                    <label className="font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)] block mb-1">
                      Monthly messages
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={limitMonthlyMessages}
                      onChange={(e) => setLimitMonthlyMessages(e.target.value)}
                      placeholder="e.g. 2500"
                      data-testid="limit-monthly-messages-input"
                      className="w-full bg-transparent border-b border-[var(--border)] focus:border-[var(--accent)] outline-none py-2 font-mono-ui text-sm transition-colors"
                    />
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 items-center mt-4">
                  <button
                    onClick={saveLimits}
                    disabled={limitBusy}
                    data-testid="limits-save"
                    className="px-4 py-2 font-mono-ui text-[10px] tracking-[0.28em] uppercase border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-[var(--bg)] disabled:opacity-40 transition-colors inline-flex items-center gap-2"
                  >
                    {limitBusy && <Loader2 size={12} className="animate-spin" />}
                    Save limits
                  </button>
                  <button
                    onClick={clearLimits}
                    disabled={limitBusy}
                    data-testid="limits-clear"
                    className="px-4 py-2 font-mono-ui text-[10px] tracking-[0.28em] uppercase border border-[var(--border)] text-[var(--text-muted)] hover:border-red-400 hover:text-red-400 disabled:opacity-40 transition-colors"
                  >
                    Clear (revert to default)
                  </button>
                  {limitMsg && (
                    <span
                      data-testid="limits-msg"
                      className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-muted)]"
                    >
                      {limitMsg}
                    </span>
                  )}
                </div>
                <div className="mt-3 font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--text-dim)]">
                  Currently:{" "}
                  {userProfile.user.limits?.monthly_token_cap
                    ? `${userProfile.user.limits.monthly_token_cap.toLocaleString()} tokens/mo`
                    : "default (1,000,000 tokens/mo)"}{" "}
                  ·{" "}
                  {userProfile.user.limits?.daily_message_cap
                    ? `${userProfile.user.limits.daily_message_cap} queries/day`
                    : "default (10 queries/day)"}
                  {" "}·{" "}
                  {userProfile.user.limits?.monthly_message_cap
                    ? `${userProfile.user.limits.monthly_message_cap.toLocaleString()} messages/mo`
                    : "no monthly message override"}
                </div>
              </div>

              {/* Retention email */}
              <div className="border border-[var(--border)] p-4" data-testid="retention-email">
                <div className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-muted)] mb-1">
                  Nudge / retention email
                </div>
                <p className="font-sans text-xs text-[var(--text-dim)] mb-4 max-w-2xl">
                  Send this user a one-click templated email from{" "}
                  <span className="font-mono-ui">support@humanebehavior.com</span>. Delivered via Resend.
                </p>
                <div className="flex flex-wrap gap-2 items-center">
                  <button
                    onClick={() => sendRetentionEmail("upgrade")}
                    disabled={emailBusy}
                    data-testid="email-upgrade"
                    className="px-4 py-2 font-mono-ui text-[10px] tracking-[0.28em] uppercase border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-[var(--bg)] disabled:opacity-40 transition-colors"
                  >
                    Send upgrade nudge
                  </button>
                  <button
                    onClick={() => sendRetentionEmail("reengage")}
                    disabled={emailBusy}
                    data-testid="email-reengage"
                    className="px-4 py-2 font-mono-ui text-[10px] tracking-[0.28em] uppercase border border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--accent)] hover:text-[var(--accent)] disabled:opacity-40 transition-colors"
                  >
                    Send re-engage
                  </button>
                  <button
                    onClick={() => sendRetentionEmail("thanks")}
                    disabled={emailBusy}
                    data-testid="email-thanks"
                    className="px-4 py-2 font-mono-ui text-[10px] tracking-[0.28em] uppercase border border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--accent)] hover:text-[var(--accent)] disabled:opacity-40 transition-colors"
                  >
                    Send thank-you
                  </button>
                  {emailBusy && <Loader2 size={12} className="animate-spin text-[var(--text-muted)]" />}
                  {emailMsg && (
                    <span
                      data-testid="email-msg"
                      className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-muted)]"
                    >
                      {emailMsg}
                    </span>
                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="border border-[var(--border)] p-4">
                  <div className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-muted)] mb-2">
                    Locations
                  </div>
                  <div className="font-sans text-sm">
                    <div className="mb-2">
                      <span className="font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)] mr-2">
                        Countries
                      </span>
                      {userProfile.locations.countries.length > 0
                        ? userProfile.locations.countries.join(", ")
                        : <span className="text-[var(--text-dim)]">unknown</span>}
                    </div>
                    <div>
                      <span className="font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)] mr-2">
                        IPs (last 30d)
                      </span>
                      <span className="text-[var(--text-muted)] break-all">
                        {userProfile.locations.ips.length > 0
                          ? userProfile.locations.ips.join(" · ")
                          : "none tracked"}
                      </span>
                    </div>
                    <div className="mt-2">
                      <span className="font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)] mr-2">
                        Last active
                      </span>
                      {userProfile.activity.last_active
                        ? userProfile.activity.last_active.replace("T", " ").slice(0, 19)
                        : <span className="text-[var(--text-dim)]">unknown</span>}
                    </div>
                  </div>
                </div>

                <div className="border border-[var(--border)] p-4">
                  <div className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-muted)] mb-2">
                    Recent chats
                  </div>
                  <div className="space-y-2 max-h-72 overflow-y-auto">
                    {(userProfile.recent_chats || []).length === 0 && (
                      <p className="font-sans text-sm text-[var(--text-dim)]">No chats recorded.</p>
                    )}
                    {(userProfile.recent_chats || []).map((c, i) => (
                      <div key={i} className="border-l-2 border-[var(--border)] pl-3 py-1">
                        <div className="font-mono-ui text-[9px] tracking-[0.18em] uppercase text-[var(--text-dim)]">
                          {c.created_at?.replace?.("T", " ").slice(0, 19)}
                          {c.domain && ` · ${c.domain}`}
                          {c.risk && ` · ${c.risk}`}
                        </div>
                        <div className="font-sans text-sm text-[var(--text)] truncate">
                          {c.query_preview || "(empty)"}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </Card>

        <Card
          title="Session replay"
          testid="section-session-replay"
          description="Anonymised route + click journey for every recent session. Click a row to walk through the visitor's timeline — where they landed, what they clicked, and where they dropped off."
        >
          <div className="grid md:grid-cols-2 gap-4">
            <div className="max-h-96 overflow-y-auto border border-[var(--border)]" data-testid="session-list">
              <table className="w-full font-mono-ui text-[11px]">
                <thead className="sticky top-0 bg-[var(--bg)]">
                  <tr className="text-[var(--text-dim)] tracking-[0.2em] uppercase text-[9px]">
                    <th className="text-left py-2 px-2">When</th>
                    <th className="text-left py-2 px-2">Who</th>
                    <th className="text-right py-2 px-2">Pages</th>
                    <th className="text-right py-2 px-2">Clicks</th>
                  </tr>
                </thead>
                <tbody>
                  {(sessionsList?.sessions || []).map((s) => (
                    <tr
                      key={s.session_id}
                      data-testid={`session-row-${s.session_id}`}
                      onClick={() => openSession(s.session_id)}
                      className={`border-t border-[var(--border)]/60 cursor-pointer hover:bg-[var(--border)]/20 ${
                        selectedSession === s.session_id ? "bg-[var(--accent)]/10" : ""
                      }`}
                    >
                      <td className="py-1.5 px-2 text-[var(--text-dim)]">
                        {s.last_seen?.slice?.(5, 16).replace("T", " ")}
                      </td>
                      <td className="py-1.5 px-2">
                        <div className="text-[var(--text)] truncate max-w-[160px]">
                          {s.email || "anon"}
                        </div>
                        <div className="text-[9px] text-[var(--text-dim)] tracking-[0.14em] uppercase">
                          {s.city ? `${s.city}, ${s.country}` : s.country}
                        </div>
                      </td>
                      <td className="py-1.5 px-2 text-right">{s.pages}</td>
                      <td className="py-1.5 px-2 text-right">{s.clicks}</td>
                    </tr>
                  ))}
                  {(sessionsList?.sessions || []).length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-4 text-center text-[var(--text-dim)]">
                        No sessions in the last 7 days.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="border border-[var(--border)] p-4 max-h-96 overflow-y-auto" data-testid="session-replay">
              {!selectedSession && (
                <p className="font-sans text-sm text-[var(--text-dim)]">
                  Pick a session on the left to replay its journey.
                </p>
              )}
              {selectedSession && !sessionEvents && (
                <p className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text-muted)]">
                  <Loader2 size={12} className="inline animate-spin mr-2" /> Loading…
                </p>
              )}
              {sessionEvents?.error && (
                <p className="font-sans text-sm text-red-400">{sessionEvents.error}</p>
              )}
              {sessionEvents && !sessionEvents.error && (
                <>
                  <div className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-muted)] mb-3">
                    {sessionEvents.meta?.email || "anon"} · {sessionEvents.meta?.city},{" "}
                    {sessionEvents.meta?.country} · {sessionEvents.meta?.device}/
                    {sessionEvents.meta?.browser} · {sessionEvents.events.length} events
                  </div>
                  <ol className="space-y-2">
                    {sessionEvents.events.map((ev, i) => (
                      <li key={i} className="border-l-2 border-[var(--border)] pl-3 py-1">
                        <div className="font-mono-ui text-[9px] tracking-[0.2em] uppercase text-[var(--text-dim)]">
                          {ev.ts?.slice?.(11, 19)} · {ev.type}
                        </div>
                        {ev.type === "pageview" ? (
                          <div className="font-sans text-sm">
                            <span className="text-[var(--accent)]">→</span> {ev.path}
                            {ev.referrer && (
                              <span className="text-[var(--text-dim)] ml-2">from {ev.referrer}</span>
                            )}
                          </div>
                        ) : (
                          <div className="font-sans text-sm">
                            <span className="text-[var(--text-muted)]">clicked</span>{" "}
                            <span className="font-mono-ui text-xs">{ev.target}</span>
                            {ev.text && (
                              <span className="text-[var(--text-muted)] ml-2">&ldquo;{ev.text}&rdquo;</span>
                            )}
                          </div>
                        )}
                      </li>
                    ))}
                  </ol>
                </>
              )}
            </div>
          </div>
        </Card>
      </main>
    </div>
  );
}
